/**
 * Build-time app config (Vite import.meta.env), baked into the bundle.
 *
 * Replaces the old runtime fetch('/env.json') + window.__ENV__ pattern: the
 * values were always fixed at build time, so fetching them at runtime only
 * added a render-blocking round-trip and a white-screen failure mode.
 *
 * Sources: apps/guide/.env.local, written by `node scripts/generate-env.js
 * <stage> guide` (local dev reads env/guide.<stage>.json; CI/Cloudflare Pages
 * reads the ENV_JSON_GUIDE variable). All values here are public-by-design —
 * they ship in browser JS either way.
 *
 * window.__ENV__ is still assigned (synchronously) from this object in
 * main.tsx as a transitional shim for existing read sites. New code should
 * import { config } from this module instead.
 */

export type GuideConfig = {
  STAGE: string;
  SUPABASE_URL: string;
  SUPABASE_ANON_KEY: string;
  EDGE_BASE?: string;
  SSE_URL?: string;
  SEEKER_BASE_SHARE_URL?: string;
  POSTHOG_PUBLIC_KEY?: string;
  POSTHOG_PUBLIC_HOST?: string;
  SITE_URL?: string;
  SEEKER_URL?: string;
  TURNSTILE_SITE_KEY?: string;
  CHAMELEON_TOKEN?: string;
};

const env = import.meta.env;

export const config: GuideConfig = {
  STAGE: env.VITE_STAGE ?? '',
  SUPABASE_URL: env.VITE_SUPABASE_URL ?? '',
  SUPABASE_ANON_KEY: env.VITE_SUPABASE_ANON_KEY ?? '',
  EDGE_BASE: env.VITE_EDGE_BASE,
  SSE_URL: env.VITE_SSE_URL,
  SEEKER_BASE_SHARE_URL: env.VITE_SEEKER_BASE_SHARE_URL,
  POSTHOG_PUBLIC_KEY: env.VITE_POSTHOG_PUBLIC_KEY,
  POSTHOG_PUBLIC_HOST: env.VITE_POSTHOG_PUBLIC_HOST,
  SITE_URL: env.VITE_SITE_URL,
  SEEKER_URL: env.VITE_SEEKER_URL,
  TURNSTILE_SITE_KEY: env.VITE_TURNSTILE_SITE_KEY,
  CHAMELEON_TOKEN: env.VITE_CHAMELEON_TOKEN,
};

const required: (keyof GuideConfig)[] = ['STAGE', 'SUPABASE_URL', 'SUPABASE_ANON_KEY'];
const missing = required.filter((k) => !config[k]);
if (missing.length > 0) {
  // A build-time mistake (generate-env.js also guards this); fail loudly.
  throw new Error(
    `Missing required build-time config: ${missing.join(', ')}. ` +
      'Run `node scripts/generate-env.js <stage> guide` before building.'
  );
}

declare global {
  interface Window {
    /** Transitional shim — assigned from `config` in main.tsx. Prefer importing { config }. */
    __ENV__: GuideConfig;
  }
}
